import { useEffect, useState } from "react";
import { Alert, AlertTitle, Typography } from "@mui/material";
import { useTranslation } from "i18n-fe";
import { findStuckStations } from "../util/stationMonitor";
import { formatElapsedLabel } from "../util/scribePresence";
import StationExceptionLine from "./StationExceptionLine";
import BannerList from "./BannerList";

// A plain ticking clock (same pattern as StationMonitor.jsx's useNow), so a
// station crosses the threshold on screen without waiting for the next poll.
function useNow(intervalMs) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

const CLOCK_TICK_MS = 5000;

// Four static keys (never built from parts), so the i18n extractor sees each.
function stuckLine(t, s, time) {
  const vars = { station: s.stationLabel, ring: s.ring, time };
  if (s.stationLabel) {
    return s.ring ? t("stationMonitor.stuck.lineRing", vars) : t("stationMonitor.stuck.line", vars);
  }
  return s.ring ? t("stationMonitor.stuck.lineUnnamedRing", vars) : t("stationMonitor.stuck.lineUnnamed", vars);
}

// "pine-frog (\u20267f3a):" / "a (\u2026x), b (\u2026y):" / "A station not yet named:".
function stationList(t, list) {
  const names = list.map((s) => s.stationLabel || t("stationMonitor.stuck.stationNameUnnamed"));
  return `${names.join(", ")}:`;
}

// #3427 -- one banner naming every station whose oldest unshipped score has
// waited past the threshold (default 3 minutes) and how long. Rendered by both
// the dashboard strip and the Station Monitor page, from the same server rows,
// so the two can never disagree. Renders nothing when no station is stuck, so
// it disappears as soon as the op ships and the next read comes back clean.
// The matching server-side alert (once per breach, to chat) is score-api's
// lib/stuck-station-alert.js.
export default function StuckStationBanner({ rows, thresholdMs, compact = false }) {
  const { t } = useTranslation("scoreadmin");
  const now = useNow(CLOCK_TICK_MS);
  const stuck = findStuckStations(rows, now, thresholdMs);
  if (stuck.length === 0) return null;
  // #3646: the dashboard shows one line (count + the longest wait); the Station
  // Monitor page keeps the full banner below.
  if (compact) {
    const text = t("stationMonitor.stuck.compact", {
      count: stuck.length,
      time: formatElapsedLabel(Math.max(...stuck.map((s) => s.ageMs))),
    });
    return <StationExceptionLine severity="error" variant="filled" testId="stuck-station-banner" text={text} />;
  }
  const live = stuck.filter((s) => s.live);
  const offline = stuck.filter((s) => !s.live);
  const mixed = live.length > 0 && offline.length > 0;

  return (
    <Alert severity="error" variant="filled" role="alert" data-testid="stuck-station-banner" sx={{ mb: 2 }}>
      <AlertTitle>{t("stationMonitor.stuck.title")}</AlertTitle>
      {/* #3682: at most 3 stations, then "and N more". */}
      <BannerList
        testId="stuck-station-list"
        items={stuck}
        renderItem={(s) => (
          <Typography variant="body2" data-testid={`stuck-station-${s.key}`}>
            {stuckLine(t, s, formatElapsedLabel(s.ageMs))}
          </Typography>
        )}
      />
      {/* #3525: the next step differs by whether the tablet is calling in. A
          station that is not live can be cleared from its row; a live one
          cannot (its next contact would un-clear it), so its scribe retries
          from the tablet's own Outbox. When both kinds are stuck at once each
          hint is prefixed with the stations it is about. */}
      {offline.length > 0 && (
        <Typography variant="body2" sx={{ mt: 0.5 }} data-testid="stuck-station-hint-offline">
          {mixed && `${stationList(t, offline)} `}
          {t("stationMonitor.stuck.hint")}
        </Typography>
      )}
      {live.length > 0 && (
        <Typography variant="body2" sx={{ mt: 0.5 }} data-testid="stuck-station-hint-live">
          {mixed && `${stationList(t, live)} `}
          {t("stationMonitor.stuck.hintLive")}
        </Typography>
      )}
    </Alert>
  );
}
